import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, ParamMap, Params, Router } from '@angular/router';
import { AlertController, IonicModule, LoadingController, NavController, PopoverController } from '@ionic/angular';
import { NgxTippyModule } from 'ngx-tippy-wrapper';
import { Subject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';

import {
  ActionDropdownComponent,
  ActionDropdownType,
} from 'src/app/components/action-dropdown/action-dropdown.component';
import { ArticleLocationDisplayComponent } from 'src/app/components/article-location-display/article-location-display.component';
import { CollectionSelectorComponent } from 'src/app/components/collection-selector/collection-selector.component';
import { isDev } from 'src/app/utils/utils';
import { KnowledgeBaseArticle } from 'src/models';
import { ENTITY_TYPES } from 'src/models/entities';
import { AuthService } from 'src/services/auth.service';
import { ConversationsService } from 'src/services/conversations.service';
import { KnowledgeBaseArticlesService } from 'src/services/knowledge-base-articles.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

import { KnowledgeBaseEditorComponent } from './knowledge-base-editor/knowledge-base-editor.component';

@Component({
  selector: 'app-knowledge-base-article-edit',
  templateUrl: './knowledge-base-article-edit.component.html',
  styleUrls: ['./knowledge-base-article-edit.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    NgxTippyModule,
    KnowledgeBaseEditorComponent,
    ArticleLocationDisplayComponent,
  ],
})
export class KnowledgeBaseArticleEditComponent implements OnInit, OnDestroy {
  @ViewChild('editorContainer') editorContainer: KnowledgeBaseEditorComponent;
  @ViewChild('titleInput') titleInput: any;

  isDev = isDev;

  articleId: string | null = null;
  isNewArticle = true;
  isLoading = false;
  isGeneratingArticle = false;

  title: string = '';
  url: string | null = null;
  status: 'active' | 'inactive' = 'active';
  userGroups: any[] = [];
  channels: any[] = [];
  collectionId: string | null = null;

  suggestionOriginConversationId: string | null = null;

  originPage: string | null = null;

  // Track changes functionality
  trackChangesEnabled: boolean = false;
  suggestionsMode: boolean = false;
  simulatedBackendContent: string = '';
  diffModeEnabled: boolean = false;
  comparisonContent: string = '';
  hasOutstandingDiffs: boolean = false;

  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private alertCtrl: AlertController,
    private loadingCtrl: LoadingController,
    private popoverCtrl: PopoverController,
    private navController: NavController,
    private msgSrvc: MessageService,
    private knowledgeBaseArticlesService: KnowledgeBaseArticlesService,
    private conversationsService: ConversationsService,
    private searchablePopoverService: SearchablePopoverService,
    public authService: AuthService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.route.params
      .pipe(
        switchMap((params: Params) => {
          const id = params['id'];
          return this.route.queryParamMap.pipe(
            map((queryParams: ParamMap) => ({
              id,
              conversationId: queryParams.get('conversationId'),
              prefillTitle: queryParams.get('prefillTitle'),
              prefillContent: queryParams.get('prefillContent'),
              originPage: queryParams.get('from'),
              collectionId: queryParams.get('collectionId'),
            })),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(async ({ id, conversationId, prefillTitle, prefillContent, originPage, collectionId }) => {
        this.resetComponentState();

        this.articleId = id;
        this.isNewArticle = !this.articleId;
        this.originPage = originPage;

        if (this.articleId) {
          await this.loadKnowledgeBaseArticle(this.articleId);
        } else {
          if (collectionId) {
            this.collectionId = collectionId;
          }
          if (conversationId) {
            await this.loadSuggestionData(conversationId);
          } else if (prefillTitle || prefillContent) {
            this.title = prefillTitle ?? '';
            const content = prefillContent ?? '';
            setTimeout(() => this.editorContainer?.setContent(content), 0);
            // Set the original content in textarea for track changes comparison
            this.simulatedBackendContent = content;
          }
          setTimeout(() => this.titleInput?.setFocus(), 500);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resetComponentState(): void {
    this.articleId = null;
    this.isNewArticle = true;
    this.isLoading = false;
    this.isGeneratingArticle = false;
    this.title = '';
    this.url = null;
    this.status = 'active';
    this.userGroups = [];
    this.channels = [];
    this.collectionId = null;
    this.suggestionOriginConversationId = null;
    this.originPage = null;
    this.editorContainer?.clearContent();
    // Reset track changes
    this.trackChangesEnabled = false;
    this.suggestionsMode = false;
    this.simulatedBackendContent = '';
    this.diffModeEnabled = false;
    this.comparisonContent = '';
  }

  async loadKnowledgeBaseArticle(id: string): Promise<void> {
    this.isLoading = true;
    this.title = 'Loading...';
    this.url = null;
    this.status = 'active';
    this.userGroups = [];
    this.channels = [];
    this.collectionId = null;
    this.editorContainer?.clearContent();

    try {
      const knowledgeBaseArticle = await this.knowledgeBaseArticlesService.getById(id);
      this.title = knowledgeBaseArticle.title;
      this.url = knowledgeBaseArticle.url;
      this.status = (knowledgeBaseArticle.status as 'active' | 'inactive') ?? 'active';
      this.userGroups = knowledgeBaseArticle.userGroups ?? [];
      this.channels = knowledgeBaseArticle.channels ?? [];
      this.collectionId = knowledgeBaseArticle.collectionId ?? null;

      this.editorContainer?.setContent(knowledgeBaseArticle.content);

      // Set the original content in textarea for track changes comparison
      this.simulatedBackendContent = knowledgeBaseArticle.content;
    } catch (error) {
      console.error('Failed to load article:', error);
      this.articleId = undefined;
      this.isNewArticle = true;
      await this.msgSrvc.showError('Failed to load article.');
      this.navigateBack();
    } finally {
      this.isLoading = false;
    }
  }

  async loadSuggestionData(conversationId: string): Promise<void> {
    this.isGeneratingArticle = true;
    this.title = 'Generating...';
    const path = `/dashboard/inbox/all/conversations/${conversationId}`;
    this.url = `${window.location.origin}${path}`;
    this.suggestionOriginConversationId = conversationId;
    this.editorContainer?.setContent('');
    try {
      const suggestion = await this.conversationsService.getKnowledgeBaseArticleSuggestion(conversationId);
      this.title = suggestion.title;

      this.editorContainer?.setContent(suggestion.content);

      // Set the original content in textarea for track changes comparison
      this.simulatedBackendContent = suggestion.content;
    } catch (error) {
      await this.msgSrvc.showError('Failed to generate article.');
    } finally {
      this.isGeneratingArticle = false;
    }
  }

  async saveArticle(): Promise<void> {
    if (!this.title) {
      await this.msgSrvc.show('Whoops! Please enter a title.');
      this.titleInput?.setFocus();
      return;
    }

    const currentEditorContent = this.editorContainer?.getContent();
    if (!currentEditorContent) {
      await this.msgSrvc.show('Whoops! Please enter some content.');
      this.editorContainer?.focus('end');
      return;
    }

    // Check for outstanding diffs
    if (this.hasOutstandingDiffs) {
      await this.msgSrvc.show('Please approve or reject all outstanding changes before saving.');
      return;
    }

    this.isLoading = true;

    try {
      const articleData: Partial<KnowledgeBaseArticle> = {
        title: this.title,
        content: currentEditorContent,
        url: this.url || null,
        status: this.status,
        userGroupIds: this.userGroups?.map((userGroup) => userGroup.id),
        channelIds: this.channels?.map((channel) => channel.id),
        collectionId: this.collectionId,
      };

      if (this.isNewArticle && this.suggestionOriginConversationId) {
        articleData.slackCxTicketId = this.suggestionOriginConversationId;
      }

      if (articleData.collectionId === undefined) {
        articleData.collectionId = null;
      }

      if (this.isNewArticle) {
        delete articleData.id;
        const result = await this.knowledgeBaseArticlesService.create({
          ...articleData,
          projectId: this.authService.selectedProject?.id ?? null,
        } as KnowledgeBaseArticle);
        await this.msgSrvc.show('Article added!');
        this.articleId = result.id;
        this.collectionId = result.collectionId ?? null;
        this.isNewArticle = false;
        this.navController.navigateForward(['/dashboard/knowledge/articles', this.articleId, 'edit'], {
          replaceUrl: true,
          animated: false,
        });
      } else if (this.articleId) {
        delete articleData.id;
        await this.knowledgeBaseArticlesService.updatePartial(this.articleId, articleData);
        await this.msgSrvc.show('Article updated!');
      }
    } catch (error) {
      console.error('Failed to save article:', error);
      await this.msgSrvc.showError(this.isNewArticle ? 'Failed to create article.' : 'Failed to update article.');
    } finally {
      this.isLoading = false;
    }
  }

  async deleteArticle(): Promise<void> {
    if (!this.articleId) {
      return;
    }

    const alert = await this.alertCtrl.create({
      header: 'Are you sure?',
      subHeader: `This can't be undone`,
      buttons: [
        {
          text: 'Nevermind',
          role: 'cancel',
        },
        {
          text: 'Delete',
          handler: async () => {
            this.isLoading = true;
            const loading = await this.loadingCtrl.create({ message: 'Deleting Article...' });
            await loading.present();
            try {
              await this.knowledgeBaseArticlesService.delete(this.articleId!);
              await this.msgSrvc.show('Article deleted!');
              this.navigateBack();
            } catch (e) {
              await this.msgSrvc.showError('Unable to delete article.');
            } finally {
              this.isLoading = false;
              await loading.dismiss();
            }
          },
        },
      ],
    });
    await alert.present();
  }

  navigateBack(): void {
    const currentPath = window.location.pathname;
    this.navController.pop();

    // If nav controller can't pop, we still want to send them back to the inbox
    // Nav controller doesn't seem to have a way to easily detect whether we _can_ pop
    // So we're just going to hackily check whether we did pop.
    setTimeout(() => {
      if (currentPath === window.location.pathname) {
        this.navController.navigateRoot('/dashboard/knowledge/articles');
      }
    }, 300);
  }

  async showArticleActions(event: MouseEvent) {
    const LABEL = {
      DELETE: 'Delete article',
    };

    const options: ActionDropdownType[] = [
      {
        label: LABEL.DELETE,
        icon: 'trash-outline',
        destructive: true,
      },
    ];

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      event,
      showBackdrop: false,
      componentProps: {
        options,
        callback: (_idx: number, label: string) => {
          popover.dismiss();

          if (label === LABEL.DELETE) {
            this.deleteArticle();
          }
        },
      },
    });
    popover.present();
  }

  addUserGroup(event: MouseEvent) {
    this.searchablePopoverService.entities({
      event,
      type: ENTITY_TYPES.USER_GROUP,
      excludeIds: this.userGroups?.map((userGroup) => userGroup.id),
      callback: async (id: string, name: string) => {
        if (id && !this.userGroups.some((ug) => ug.id === id)) {
          this.userGroups = [...(this.userGroups ?? []), { id, name }];
        }
      },
    });
  }

  removeUserGroup(groupIdToRemove: string) {
    if (!this.articleId) {
      return;
    }

    this.userGroups = this.userGroups.filter((group) => group.id !== groupIdToRemove);
  }

  addChannel(event: MouseEvent) {
    this.searchablePopoverService.customers({
      event,
      excludeIds: this.channels?.map((channel) => channel.id),
      callback: async (id: string, name: string) => {
        if (id && !this.channels?.some((c) => c.id === id)) {
          this.channels = [...(this.channels ?? []), { id, name }];
        }
      },
    });
  }

  removeChannel(channelIdToRemove: string) {
    if (!this.articleId) {
      return;
    }

    this.channels = this.channels.filter((channel) => channel.id !== channelIdToRemove);
  }

  handleTitleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === 'ArrowDown') {
      event.preventDefault();
      if (this.editorContainer) {
        this.editorContainer.focus('start');
      }
    }
  }

  onFocusTitleRequested(): void {
    const titleIonInput = this.titleInput;
    if (titleIonInput) {
      titleIonInput.setFocus();
      setTimeout(async () => {
        try {
          const nativeInput: HTMLInputElement | null = await (titleIonInput.getInputElement
            ? titleIonInput.getInputElement()
            : titleIonInput.el?.querySelector('input'));
          if (nativeInput) {
            nativeInput.setSelectionRange(0, 0);
          }
        } catch (e) {
          console.error('Error accessing native input or setting selection:', e);
        }
      }, 50);
    } else {
      console.warn('Could not find title input element to focus.');
    }
  }

  async showCollectionSelector(event: MouseEvent) {
    const popover = await this.popoverCtrl.create({
      component: CollectionSelectorComponent,
      event: event,
      componentProps: {
        currentCollectionId: this.collectionId,
        callback: (selectedId: string | null) => {
          if (this.collectionId !== selectedId) {
            this.collectionId = selectedId;
          }
        },
      },
    });

    await popover.present();
  }

  // Add trackBy function for user groups
  trackByUserGroupId(index: number, userGroup: any): string {
    return userGroup.id;
  }

  trackByChannelId(index: number, channel: any): string {
    return channel.id;
  }

  // Track Changes Methods
  toggleTrackChanges(): void {
    this.trackChangesEnabled = !this.trackChangesEnabled;
    this.suggestionsMode = this.trackChangesEnabled;
  }

  compareAndShowChanges(): void {
    if (!this.trackChangesEnabled || !this.simulatedBackendContent) {
      this.msgSrvc.showError('Please enable track changes and provide original content');
      return;
    }

    if (!this.editorContainer?.editor) {
      console.warn('Editor not ready');
      return;
    }

    const editor = this.editorContainer.editor;

    // Get the current editor JSON directly (ORIGINAL)
    const currentEditorJson = editor.getJSON();

    // Store current editor content
    const originalContent = this.editorContainer.getContent() || '';

    // Set the simulated content from textarea to get its JSON (MODIFIED)
    editor.commands.setContent(this.simulatedBackendContent, false);
    const simulatedContentJson = editor.getJSON();

    // Restore the original editor content
    editor.commands.setContent(originalContent, false);

    // Show diff: pass JSON objects directly to a new command
    const success = editor.commands.showDocumentDiffFromJSON({
      originalDoc: currentEditorJson,
      modifiedDoc: simulatedContentJson,
    });

    if (success) {
      this.msgSrvc.show(
        'Document comparison complete. Changes are highlighted in green (additions) and red (deletions).',
      );
    } else {
      this.msgSrvc.showError('Failed to compute document differences');
    }
  }

  onOutstandingDiffsChange(hasOutstanding: boolean): void {
    this.hasOutstandingDiffs = hasOutstanding;
  }
}
